import React from "react";
import styled from "styled-components";
// import dayjs from "dayjs";
import { getLocale } from "react-i18nify";

import {
  Box,
  Grid,
  Stack,
  Typography,
  styled as muiStyled,
} from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";

import font from "src/styles/font";
import device from "src/styles/device";
import { convertIntToCurrency } from "src/common";
import { Black, DarkGrey, GreyStroke, White, White01 } from "src/styles/color";

import LabelSection from "src/components/core/LabelSection";

const SectionBox = styled.div`
  width: 100%;
  background: ${White};
  /* border: 1px solid ${GreyStroke}; */
  border-top: 12px solid ${({ fill }) => fill || GreyStroke};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 8px 20px 16px;

  @media ${device.mobileL} {
    padding: 12px 16px;
  }
`;

const SpanSubTitle = styled.div`
  font-size: ${font.size.sm};
  font-weight: 500;
  line-height: 20px;
  color: ${DarkGrey};
  margin-bottom: 16px;
  border-bottom: 1px solid ${GreyStroke};

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
  }
`;

const ItemCountBox = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 10px auto 50px;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SpanCount = styled.p`
  font-size: ${font.size.xs};
  font-weight: 500;
  line-height: 12px;
  color: ${({ color }) => color || DarkGrey};
  margin: 0;
  min-width: ${({ minWidth }) => `${minWidth}px` || "auto"};
  text-align: ${({ align }) => align || "left"};
`;

const BorderLinearProgress = muiStyled(LinearProgress)(
  ({ theme, barColor }) => ({
    height: 3,
    width: "100%",
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: White01,
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 10,
      backgroundColor: barColor || "#1a90ff",
    },
  }),
);

const featureData = {
  copySpec: {
    en: "Copy specification",
    th: "การคัดลอกสเปค",
  },
  compareProduct: {
    en: "Compare products",
    th: "การกดเปรียบเทียบสินค้า",
  },
  addToSpecsheet: {
    en: "Add to specsheet",
    th: "การเพิ่มสินค้าลงใน specsheet",
  },
};
const fillColors = ["#E13B30", "#C09020", "#005FCC", "#219653"];

const UsageProductFeatures = ({ data = [], selectedTimeRangeData }) => {
  return (
    <LabelSection
      title={
        getLocale() === "th" ? "การใช้งาน Feature" : "Usage of Product Features"
      }
      description={
        <Typography
          variant="body2"
          sx={{
            ml: 2.5,
            "&.MuiTypography-root": { fontSize: font.size.xs },
          }}
        >
          {getLocale() === "th"
            ? `${selectedTimeRangeData?.label?.th} · ทุกร้านค้าบนแพลตฟอร์ม`
            : `${selectedTimeRangeData?.label?.en} · All stores on the platform`}
        </Typography>
      }
      style={{ marginBottom: 0 }}
      titleStyle={{ fontSize: font.size.lg, fontWeight: 600 }}
      headerStyle={{ marginBottom: 16 }}
    >
      <Grid container spacing={1.5}>
        {Object.keys(data).length > 0 ? (
          Object.keys(data).map((featureName, index) => {
            let item = data[featureName];
            let fill = fillColors[index % fillColors.length];
            let topProductCount = item?.topItems?.[0]?.eventCount || 0;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <SectionBox fill={fill}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {featureData[featureName][getLocale() || "en"]}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    {convertIntToCurrency(item?.total, false)}
                    <span> {getLocale() === "th" ? "ครั้ง" : "times"}</span>
                  </Typography>

                  <SpanSubTitle>สินค้าที่ถูกใช้งานมากสุด</SpanSubTitle>

                  {item?.topItems?.length > 0 ? (
                    item?.topItems?.map((product, idx) => {
                      let percentage =
                        (product.eventCount / topProductCount) * 100;
                      return (
                        <ItemCountBox key={idx}>
                          <SpanCount
                            minWidth={10}
                            color={idx === 0 ? fill : DarkGrey}
                          >
                            {idx + 1}
                          </SpanCount>

                          <Stack flexWrap="wrap" spacing={0.5}>
                            <SpanCount color={Black}>
                              {product?.itemName || "N/A"}
                            </SpanCount>
                            <BorderLinearProgress
                              variant="determinate"
                              value={percentage}
                              barColor={fill}
                            />
                          </Stack>

                          <SpanCount minWidth={50} align="right">
                            {convertIntToCurrency(product.eventCount, false)}{" "}
                            {getLocale() === "th" ? "ครั้ง" : "times"}
                          </SpanCount>
                        </ItemCountBox>
                      );
                    })
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 100,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          "&.MuiTypography-root": {
                            fontSize: font.size.sm,
                            color: DarkGrey,
                          },
                        }}
                      >
                        {getLocale() === "th"
                          ? "ไม่มีข้อมูล"
                          : "No data available"}
                      </Typography>
                    </Box>
                  )}
                </SectionBox>
              </Grid>
            );
          })
        ) : (
          <></>
        )}
      </Grid>
    </LabelSection>
  );
};

export default UsageProductFeatures;
